/* eslint-disable */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { fetchWrapper } from '../common/helpers/fetch-wrapper';
import { VerifyLoginRequest } from '../models/request/verifyLoginRequest';
import { environment } from '../environments/environment';
import { ActivateAccountRequest } from '../models/request/accountRequest';
import { ForgotPasswordRequest } from '../models/request/forgotPasswordRequest';

interface AuthState {
  error: string | null;
  isUserLogged: boolean;
  manualSetupKey: string | null;
  md5Key: string | null;
  passwordConfirmed: boolean | null;
  qrCodeImage: string | null;
  token: string | null;
  activationCodeValid?: boolean
}

const initialState: AuthState = {
  token: sessionStorage.getItem('palAuth'),
  error: null,
  isUserLogged: false,
  md5Key: null,
  qrCodeImage: null,
  manualSetupKey: null,
  passwordConfirmed: null,  
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    keepLogged(state) {
      state.isUserLogged = true;
      },      
    logout(state) {
      state.md5Key = null;
      state.token = null;
      sessionStorage.removeItem('palAuth');
      state.isUserLogged = false;
    }
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      if (action.payload.loginFinished) {
        state.token = action.payload.accessToken;
        state.isUserLogged = true;
        sessionStorage.setItem('palAuth', state.token as string);
      }
      if (action.payload.md5CacheKey) {
        state.md5Key = action.payload.md5CacheKey;
        state.qrCodeImage = action.payload.qrCodeImage;
        state.manualSetupKey = action.payload.manualSetupKey;
        state.isUserLogged = false;
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.error = "invalidCredentials";
      //state.error = action.error.message || 'An error occurred during login.';
    });

    // Handle the new action for verifyTfaCode
    builder.addCase(verifyTfaCode.pending, (state) => {
      state.error = null;
    });
    builder.addCase(verifyTfaCode.fulfilled, (state, action) => {
      if (action.payload.loginFinished) {
        state.token = action.payload.accessToken;
        state.isUserLogged = true;
        sessionStorage.setItem('palAuth', state.token as string);
      }
    });

      builder.addCase(verifyTfaCode.rejected, (state, action: PayloadAction<any | null>) => {
        state.error = action.payload ? action.payload : 'Failed to verify TFA code';
    });   

    // Handle the new action for updateTfaProvider
    builder.addCase(updateTfaProvider.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateTfaProvider.fulfilled, (state, action) => {
      if (action.payload) {
        state.qrCodeImage = null;
      }
    });
    builder.addCase(updateTfaProvider.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update TFA provider';
    });

    // Handle refresh token
    builder.addCase(refreshToken.pending, (state) => {
      state.error = null;
    });
    builder.addCase(refreshToken.fulfilled, (state, action) => {
      if (action.payload) {
        // TODO: Implement.
      }
    });
    builder.addCase(refreshToken.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to refresh token';
    });

    // Handle forgot password
    builder.addCase(forgotPassword.pending, (state) => {
      state.error = null;
    });
    builder.addCase(forgotPassword.fulfilled, (state, action) => {
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to send forgot password request';
    });

    // Handle reset password
    builder.addCase(confirmNewPassword.pending, (state) => {
      state.error = null;
    });
    builder.addCase(confirmNewPassword.fulfilled, (state, action) => {
      state.passwordConfirmed = action.payload;
    });
    builder.addCase(confirmNewPassword.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to reset password';
    });

    // Handle Check Activation Code
    builder.addCase(checkActivationCode.rejected, (state, action) => {
      state.activationCodeValid = false;
      state.error = action.error.message || 'Failed to reset password';
    });
    builder.addCase(checkActivationCode.fulfilled, (state, action) => {
      state.activationCodeValid = !action.payload?.confirmed && action.payload?.username != undefined;
    });
  },
});

export const { keepLogged, logout } = authSlice.actions; // This is only for test purposes
export default authSlice.reducer;

// login thunk
export const login = createAsyncThunk('/Account/login', async ({ username, password }: { username: string, password: string }) => {
  const response = await fetchWrapper.post(`${environment.api.authService}api/Account/login`, { username, password });
  return response.data;
});

export const verifyTfaCode = createAsyncThunk(
    '/Account/verifyCode',
    async ({ verifyRequest }: { verifyRequest: VerifyLoginRequest }, { rejectWithValue }) => {
        try {
            const response = await fetchWrapper.post(`${environment.api.authService}api/Account/verifyCode`, verifyRequest);            
            if (response.success!=1) {
                const errorData = await response.error.code; 
                return rejectWithValue(errorData); 
            }
            return response.data; 
        } catch (error:any) {            
            return rejectWithValue({ message: error.message });
        }
    }
);

// updateTfaProvider thunk
export const updateTfaProvider = createAsyncThunk('/Account/updateTfaConfiguration', async ({ md5HashCode }: { md5HashCode: string }) => {
  const response = await fetchWrapper.post(`${environment.api.authService}api/Account/updateTfaConfiguration/${md5HashCode}`);
  return response.data;
});

export const refreshToken = createAsyncThunk('/Token/refreshToken', async () => {
  const response = await fetchWrapper.post(`${environment.api.authService}api/Token/refresh`);
  return response.data;
});

export const forgotPassword = createAsyncThunk(
    '/Account/forgotPassword',
    async ({ request }: { request: ForgotPasswordRequest }) => {
        const response = await fetchWrapper.post(`${environment.api.authService}api/Account/forgotpassword`,request);
        return response.data;
    });

export const confirmNewPassword = createAsyncThunk('/Account/confirmNewPassword', async ({ activateAccountRequest }: { activateAccountRequest: ActivateAccountRequest }) => {
  const response = await fetchWrapper.post(`${environment.api.authService}api/Account/confirmNewPassword`, { code: activateAccountRequest.code, password: activateAccountRequest.password });
  return response.data;
});

export const checkActivationCode = createAsyncThunk('/Account/checkActivationCode', async(activationCode: string) => {
  const response = await fetchWrapper.get(`${environment.api.authService}api/Account/checkActivationCode/${activationCode}`);
  return response;
});
